/**
 * 【AuthContext.tsx】
 * 役割：Supabase（認証基盤）を使った「ログイン・ログアウト」の状態を管理します。
 */
import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
// Supabase公式のUser型と、設定済みのクライアントを読み込みます
import { User } from "@supabase/supabase-js";
import { setCachedAccessToken, supabase } from "../supabaseClient";

/** ── 認証データの設計図 ── */
interface AuthContextType {
  user: User | null;         // ログイン中のユーザー情報
  isAuthenticated: boolean;  // ログインしているか
  isLoading: boolean;        // 認証状態を確認中か
  loginWithGoogle: () => Promise<void>; // Googleログイン実行
  logout: () => Promise<void>;          // ログアウト実行
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  loginWithGoogle: async () => { },
  logout: async () => { },
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * ── 起動時の認証状態チェック（失敗時フェイルセーフ付き） ──
   */
  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    // 追加: ネットワーク不調で getSession が返らない場合でも loading を解除する
    const sessionTimeout: ReturnType<typeof setTimeout> = setTimeout(() => {
      console.warn("[WARN] 認証セッション取得がタイムアウトしました。ゲストとして続行します。");
      setIsLoading(false);
    }, 5000);

    // 変更: 成功/失敗の両方で isLoading を解除し、画面が止まらないようにする
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        clearTimeout(sessionTimeout);
        setCachedAccessToken(session?.access_token);
        setUser(session?.user ?? null);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        clearTimeout(sessionTimeout);
        setCachedAccessToken(null);
        console.error("[ERROR] 認証セッションの取得に失敗しました:", error);
        setIsLoading(false);
      });

    // 変更: 認証状態の変化を監視し、復帰時にも loading 状態を確実に解除する
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        clearTimeout(sessionTimeout);
        setCachedAccessToken(session?.access_token);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    return () => {
      // 追加: unmount 時にタイマーを片付けて不要な state 更新を防ぐ
      clearTimeout(sessionTimeout);
      subscription.unsubscribe();
    };
  }, []);

  /** ── Googleログインの実行 ──
   * 💡 window.location.origin を使うことで、開発時は http://localhost:3000、
   *    本番時は https://pitchscout.ten-hou.com へ自動的にリダイレクト先が決まります。
   *    環境変数や手動の本番判定は不要です。
   */
  const loginWithGoogle = useCallback(async () => {
    if (!supabase) {
      console.warn("Supabase未設定のためログインできません");
      return;
    }

    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });
  }, []);

  /** ── ログアウトの実行 ── */
  const logout = useCallback(async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.warn("[WARN] Supabase ログアウトに失敗しました:", error);
    }
    setCachedAccessToken(null);
    setUser(null);
  }, []);

  /** ── パフォーマンス最適化 (useMemo) ── */
  const value = useMemo(
    () => ({ user, isAuthenticated: !!user, isLoading, loginWithGoogle, logout }),
    [user, isLoading, loginWithGoogle, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
